import React, { useState } from 'react';
import { DynamicForm, FIELD_TYPES, VALIDATION_PRESETS } from '../components/SelForm';

const Form = () => {
  const [submittedData, setSubmittedData] = useState(null);

  // Comprehensive form fields showcasing all features
  const formFields = [
    // ========== STEP 1: Personal Information ==========
    {
      id: 'firstName',
      name: 'First Name',
      type: FIELD_TYPES.TEXT,
      placeholder: 'Enter your first name',
      hint: 'Your legal first name as it appears on official documents',
      validation: {
        required: 'First name is required',
        pattern: {
          value: /^[A-Za-z\s-']+$/,
          message: 'Only letters, spaces, hyphens and apostrophes allowed'
        },
        minLength: {
          value: 2,
          message: 'First name must be at least 2 characters'
        }
      },
      step: 0,
      stepTitle: 'Personal Information',
      stepDescription: 'Basic information about you'
    },
    {
      id: 'lastName',
      name: 'Last Name',
      type: FIELD_TYPES.TEXT,
      placeholder: 'Enter your last name',
      validation: {
        required: 'Last name is required',
        minLength: {
          value: 2,
          message: 'Last name must be at least 2 characters'
        }
      },
      step: 0
    },
    {
      id: 'email',
      name: 'Email Address',
      type: FIELD_TYPES.EMAIL,
      placeholder: 'your@email.com',
      hint: 'We\'ll use this for account verification',
      validation: VALIDATION_PRESETS.email,
      step: 0
    },
    {
      id: 'phone',
      name: 'Phone Number',
      type: FIELD_TYPES.PHONE,
      placeholder: '+1 (555) 123-4567',
      validation: VALIDATION_PRESETS.phone,
      step: 0
    },
    {
      id: 'dateOfBirth',
      name: 'Date of Birth',
      type: FIELD_TYPES.DATE,
      validation: {
        required: 'Date of birth is required',
        validate: (value) => {
          const date = new Date(value);
          const today = new Date();
          const age = today.getFullYear() - date.getFullYear();
          return age >= 18 || 'You must be at least 18 years old';
        }
      },
      step: 0
    },

    // ========== STEP 2: Account Setup ==========
    {
      id: 'username',
      name: 'Username',
      type: FIELD_TYPES.TEXT,
      placeholder: 'Choose a username',
      hint: 'This will be your public display name',
      validation: {
        required: 'Username is required',
        pattern: {
          value: /^[a-zA-Z0-9_-]+$/,
          message: 'Only letters, numbers, underscores and hyphens allowed'
        },
        minLength: {
          value: 3,
          message: 'Username must be at least 3 characters'
        },
        maxLength: {
          value: 20,
          message: 'Username cannot exceed 20 characters'
        }
      },
      step: 1,
      stepTitle: 'Account Setup',
      stepDescription: 'Create your account credentials'
    },
    {
      id: 'password',
      name: 'Password',
      type: FIELD_TYPES.PASSWORD,
      placeholder: 'Create a strong password',
      hint: 'Minimum 8 characters, include uppercase, lowercase, number and symbol',
      validation: {
        required: 'Password is required',
        minLength: {
          value: 8,
          message: 'Password must be at least 8 characters'
        },
        pattern: {
          value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
          message: 'Must include uppercase, lowercase, number and special character'
        }
      },
      step: 1
    },
    {
      id: 'confirmPassword',
      name: 'Confirm Password',
      type: FIELD_TYPES.PASSWORD,
      placeholder: 'Re-enter your password',
      validation: {
        required: 'Please confirm your password',
        validate: (value, formValues) => 
          value === formValues.password || 'Passwords do not match'
      },
      step: 1
    },
    {
      id: 'accountType',
      name: 'Account Type',
      type: FIELD_TYPES.RADIO,
      options: [
        { value: 'personal', label: 'Personal Account' },
        { value: 'business', label: 'Business Account' },
        { value: 'developer', label: 'Developer Account' }
      ],
      validation: {
        required: 'Please select an account type'
      },
      step: 1
    },

    // ========== STEP 3: Profile Details ==========
    {
      id: 'profilePicture',
      name: 'Profile Picture',
      type: FIELD_TYPES.FILE,
      placeholder: 'Upload your photo',
      accept: 'image/jpeg,image/png,image/gif',
      hint: 'Max file size: 5MB. Accepted formats: JPEG, PNG, GIF',
      validation: {
        validate: (value) => {
          if (!value) return true; // Optional field
          if (value.size > 5 * 1024 * 1024) {
            return 'File size must be less than 5MB';
          }
          return true;
        }
      },
      step: 2,
      stepTitle: 'Profile Details',
      stepDescription: 'Tell us more about yourself'
    },
    {
      id: 'bio',
      name: 'Bio',
      type: FIELD_TYPES.TEXTAREA,
      placeholder: 'Tell us about yourself...',
      rows: 4,
      hint: 'Max 500 characters',
      validation: {
        maxLength: {
          value: 500,
          message: 'Bio cannot exceed 500 characters'
        }
      },
      step: 2
    },
    {
      id: 'country',
      name: 'Country',
      type: FIELD_TYPES.SELECT,
      placeholder: 'Select your country',
      options: [
        { value: 'us', label: 'United States' },
        { value: 'uk', label: 'United Kingdom' },
        { value: 'ca', label: 'Canada' },
        { value: 'au', label: 'Australia' },
        { value: 'de', label: 'Germany' },
        { value: 'fr', label: 'France' },
        { value: 'jp', label: 'Japan' },
        { value: 'other', label: 'Other' }
      ],
      validation: {
        required: 'Please select a country'
      },
      step: 2
    },
    {
      id: 'timezone',
      name: 'Timezone',
      type: FIELD_TYPES.SELECT,
      placeholder: 'Select your timezone',
      options: [
        { value: 'PST', label: 'Pacific Time (PST/PDT)' },
        { value: 'MST', label: 'Mountain Time (MST/MDT)' },
        { value: 'CST', label: 'Central Time (CST/CDT)' },
        { value: 'EST', label: 'Eastern Time (EST/EDT)' },
        { value: 'GMT', label: 'Greenwich Mean Time (GMT)' },
        { value: 'CET', label: 'Central European Time (CET)' }
      ],
      step: 2
    },

    // ========== STEP 4: Preferences & Settings ==========
    {
      id: 'experience',
      name: 'Experience Level',
      type: FIELD_TYPES.RANGE,
      min: 0,
      max: 10,
      step: 1,
      hint: 'Rate your experience level (0 = Beginner, 10 = Expert)',
      step: 3,
      stepTitle: 'Preferences',
      stepDescription: 'Customize your experience'
    },
    {
      id: 'interests',
      name: 'Areas of Interest',
      type: FIELD_TYPES.ARRAY,
      itemField: {
        type: FIELD_TYPES.TEXT,
        placeholder: 'Add an interest',
        validation: {
          required: 'Interest cannot be empty',
          minLength: {
            value: 2,
            message: 'Interest must be at least 2 characters'
          }
        }
      },
      itemLabel: 'Interest',
      hint: 'Add your professional or personal interests',
      step: 3
    },
    {
      id: 'notifications',
      name: 'Notification Preferences',
      type: FIELD_TYPES.GROUP,
      groupLabel: 'Choose your notification settings',
      fields: [
        {
          id: 'emailNotifications',
          name: 'Email Notifications',
          type: FIELD_TYPES.CHECKBOX,
          checkboxLabel: 'Receive email notifications'
        },
        {
          id: 'smsNotifications',
          name: 'SMS Notifications',
          type: FIELD_TYPES.CHECKBOX,
          checkboxLabel: 'Receive SMS notifications'
        },
        {
          id: 'pushNotifications',
          name: 'Push Notifications',
          type: FIELD_TYPES.CHECKBOX,
          checkboxLabel: 'Receive push notifications'
        },
        {
          id: 'frequency',
          name: 'Notification Frequency',
          type: FIELD_TYPES.SELECT,
          options: [
            { value: 'immediately', label: 'Immediately' },
            { value: 'daily', label: 'Daily Digest' },
            { value: 'weekly', label: 'Weekly Summary' }
          ]
        }
      ],
      step: 3
    },
    {
      id: 'theme',
      name: 'UI Theme',
      type: FIELD_TYPES.COLOR,
      hint: 'Choose your preferred accent color',
      step: 3
    },

    // ========== Conditional Fields ==========
    {
      id: 'companyName',
      name: 'Company Name',
      type: FIELD_TYPES.TEXT,
      placeholder: 'Enter your company name',
      validation: {
        required: 'Company name is required for business accounts'
      },
      condition: {
        field: 'accountType',
        operator: 'equals',
        value: 'business'
      },
      step: 1
    },
    {
      id: 'apiKey',
      name: 'API Key Request',
      type: FIELD_TYPES.CHECKBOX,
      checkboxLabel: 'I need API access',
      condition: {
        field: 'accountType',
        operator: 'equals',
        value: 'developer'
      },
      step: 1
    },
    {
      id: 'otherCountry',
      name: 'Specify Country',
      type: FIELD_TYPES.TEXT,
      placeholder: 'Enter your country',
      validation: {
        required: 'Please specify your country'
      },
      condition: {
        field: 'country',
        operator: 'equals',
        value: 'other'
      },
      step: 2
    },

    // ========== Final Step: Terms & Submission ==========
    {
      id: 'newsletter',
      name: 'Newsletter',
      type: FIELD_TYPES.CHECKBOX,
      checkboxLabel: 'Subscribe to our newsletter for updates and tips',
      step: 4,
      stepTitle: 'Final Steps',
      stepDescription: 'Review and submit your application'
    },
    {
      id: 'termsAndConditions',
      name: 'Terms and Conditions',
      type: FIELD_TYPES.CHECKBOX,
      checkboxLabel: 'I agree to the Terms of Service and Privacy Policy',
      validation: {
        required: 'You must accept the terms and conditions'
      },
      step: 4
    },
    {
      id: 'dataProcessing',
      name: 'Data Processing',
      type: FIELD_TYPES.CHECKBOX,
      checkboxLabel: 'I consent to the processing of my personal data',
      validation: {
        required: 'Data processing consent is required'
      },
      step: 4
    }
  ];

  // Form submission handler
  const handleSubmit = async (data) => {
    console.log('Form submitted:', data);
    setSubmittedData(data);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    alert('Registration successful!');
  };

  // Transform data before submission
  const transformData = (data) => {
    return {
      ...data,
      fullName: `${data.firstName} ${data.lastName}`,
      submittedAt: new Date().toISOString(),
      // Remove confirmPassword from final data
      confirmPassword: undefined
    };
  };

  // Before submit validation
  const beforeSubmit = async (data) => {
    console.log('Validating data before submission...');
    // Could make an API call to check username availability
    // await checkUsernameAvailability(data.username);
    return true; // Continue with submission
  };

  // After submit actions
  const afterSubmit = async (data) => {
    console.log('Post-submission actions...');
    // Could trigger analytics, send welcome email, etc.
  };

  // Error handler
  const handleError = (error) => {
    console.error('Form submission error:', error);
    // Could send to error tracking service
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Complete Registration Form
          </h1>
          <p className="text-gray-600">
            This example demonstrates all available form features
          </p>
        </div>

        <DynamicForm
          fields={formFields}
          formTitle="User Registration"
          formDescription="Please complete all steps to create your account"
          onSubmit={handleSubmit}
          
          // Styling
          theme="default" // or "dark" or "minimal"
          className="custom-form-class"
          columns={1} // Single column layout
          
          // Form behavior
          mode="onChange" // Validate on change
          defaultValues={{
            country: 'us',
            experience: 5,
            newsletter: true
          }}
          resetOnSubmit={false}
          
          // Features
          showProgress={true}
          
          // Buttons
          submitText="Complete Registration"
          resetText="Clear Form"
          showReset={true}
          
          // Lifecycle hooks
          transformData={transformData}
          beforeSubmit={beforeSubmit}
          afterSubmit={afterSubmit}
          onError={handleError}
          
          // Debug mode (shows form state)
          debug={true}
        />

        {/* Display submitted data */}
        {submittedData && (
          <div className="mt-8 p-6 bg-green-50 rounded-lg">
            <h3 className="text-lg font-semibold text-green-800 mb-2">
              Submitted Data:
            </h3>
            <pre className="text-sm text-gray-700 overflow-auto">
              {JSON.stringify(submittedData, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default Form;